import { FacebookRegularIcon as FacebookIcon, InstagramIcon, RedditIcon } from '@change/ds-icons';

import { SocialIcon } from '@change/ds-core-ui/next';

import type { RecommendedGroup } from '../../PostPreview/types';
import { PostPreviewContainer } from '../components/PostPreviewContainer';

import { NextSteps } from './stepsFactory';
import type { FullPostType, SharingModalFactoryProps } from './types';

type ComponentsFactoryType = {
	iconComponent: () => JSX.Element;
	modalTitle: string;
	modalDescription: string;
	postPreviewComponent: () => JSX.Element;
	nextStepsComponent: () => JSX.Element;
	secondaryCTA: string | undefined;
	primaryCTA: string;
};

// Note: The following constants are exported for testing purposes only. They should not be used outside of this file.

export const ICONS = {
	facebook: () => (
		<SocialIcon
			className="h-8 w-8"
			fullWidth={false}
			icon={<FacebookIcon />}
			aria-label="facebook icon"
			label=""
			variant="facebook"
		/>
	),
	instagram: () => (
		<SocialIcon
			className="h-8 w-8"
			fullWidth={false}
			icon={<InstagramIcon />}
			aria-label="instagram icon"
			label=""
			variant="instagram"
		/>
	),
	reddit: () => (
		<SocialIcon
			className="h-8 w-8"
			fullWidth={false}
			icon={<RedditIcon />}
			aria-label="reddit icon"
			label=""
			variant="reddit"
		/>
	),
};

export const TITLE_COPIES: Record<FullPostType, string> = {
	facebook_post: 'Your Facebook post',
	facebook_story: 'Your Facebook story',
	facebook_group_post: 'Post in Facebook group',
	instagram_post: 'Your Instagram post',
	instagram_story: 'Your Instagram story',
	reddit_post: 'Post on Reddit',
	reddit_group_post: 'Post on Reddit',
};
export const DESCRIPTION_COPIES: Record<FullPostType, string> = {
	facebook_post: 'We created this post using Facebook best practices to help you reach more supporters.',
	facebook_group_post: 'We found groups likely to support your cause and created a post for you to share.',
	facebook_story: 'We created this Story to help you share your petition and inspire others to take action.',
	instagram_post: 'We created this post using Instagram best practices to help you reach more supporters.',
	instagram_story: 'We created this Story to help you share your petition and inspire others to take action.',
	reddit_post: 'We created this post using Reddit best practices to help you reach communities.',
	reddit_group_post: 'We created this post using Reddit best practices to help you reach communities.',
};

const POST_PREVIEWS: Record<FullPostType, (props: SharingModalFactoryProps) => JSX.Element> = {
	facebook_post: (props) => {
		const postPreviewProps = {
			variant: 'facebook_post' as const,
			previewImageUrl: props.sharingResource.previewImageUrl || '',
			message: 'message' in props.sharingResource ? props.sharingResource.message : undefined,
			showDisclaimer: true,
		};
		return <PostPreviewContainer {...postPreviewProps} />;
	},
	facebook_story: (props) => {
		const postPreviewProps = {
			variant: 'facebook_story' as const,
			previewImageUrl: props.sharingResource.previewImageUrl || '',
			shareUrl: 'shareUrl' in props.sharingResource ? props.sharingResource.shareUrl : undefined,
			showDisclaimer: false,
		};
		return <PostPreviewContainer {...postPreviewProps} />;
	},
	facebook_group_post: (props) => {
		const postPreviewProps = {
			variant: 'facebook_group_post' as const,
			previewImageUrl: props.sharingResource.previewImageUrl || '',
			message: 'message' in props.sharingResource ? props.sharingResource.message : undefined,
			recommendedGroup: 'recommendedGroup' in props ? (props.recommendedGroup as RecommendedGroup) : undefined,
			showDisclaimer: true,
		};
		return <PostPreviewContainer {...postPreviewProps} />;
	},
	instagram_post: (props) => {
		const postPreviewProps = {
			variant: 'instagram_post' as const,
			previewImageUrl: props.sharingResource.previewImageUrl || '',
			message: 'message' in props.sharingResource ? props.sharingResource.message : undefined,
			showDisclaimer: true,
		};
		return <PostPreviewContainer {...postPreviewProps} />;
	},
	instagram_story: (props) => {
		const postPreviewProps = {
			variant: 'instagram_story' as const,
			previewImageUrl: props.sharingResource.previewImageUrl || '',
			shareUrl: 'shareUrl' in props.sharingResource ? props.sharingResource.shareUrl : undefined,
			showDisclaimer: false,
		};
		return <PostPreviewContainer {...postPreviewProps} />;
	},
	reddit_post: (props) => {
		const postPreviewProps = {
			variant: 'reddit_post' as const,
			previewImageUrl: props.sharingResource.previewImageUrl || '',
			message: 'message' in props.sharingResource ? props.sharingResource.message : undefined,
			recommendedGroup: 'recommendedGroup' in props ? (props.recommendedGroup as RecommendedGroup) : undefined,
			showDisclaimer: true,
			petition: props.petition,
		};
		return <PostPreviewContainer {...postPreviewProps} />;
	},
	reddit_group_post: (props) => {
		const postPreviewProps = {
			variant: 'reddit_post' as const,
			previewImageUrl: props.sharingResource.previewImageUrl || '',
			message: 'message' in props.sharingResource ? props.sharingResource.message : undefined,
			recommendedGroup: 'recommendedGroup' in props ? (props.recommendedGroup as RecommendedGroup) : undefined,
			showDisclaimer: true,
			petition: props.petition,
		};
		return <PostPreviewContainer {...postPreviewProps} />;
	},
};

export const SECONDARY_CTAS: Record<FullPostType, string | undefined> = {
	facebook_post: undefined,
	facebook_story: undefined,
	facebook_group_post: 'View group',
	instagram_post: undefined,
	instagram_story: undefined,
	reddit_post: undefined,
	reddit_group_post: 'View subreddit',
};
export const PRIMARY_CTAS: Record<FullPostType, string> = {
	facebook_post: 'Post on Facebook feed',
	facebook_story: 'Post on Facebook story',
	facebook_group_post: 'Post in group',
	instagram_post: 'Post on Instagram feed',
	instagram_story: 'Post on Instagram story',
	reddit_post: 'Post in subreddit',
	reddit_group_post: 'Post in subreddit',
};

export function componentsFactory(props: SharingModalFactoryProps): ComponentsFactoryType {
	let recommendedGroup;
	const { platform, postType } = props;

	if ('recommendedGroup' in props) {
		recommendedGroup = props.recommendedGroup;
	}

	const fullPostType = `${platform}${recommendedGroup ? '_group' : ''}_${postType}` as FullPostType;

	return {
		iconComponent: ICONS[platform],
		modalTitle: TITLE_COPIES[fullPostType],
		modalDescription: DESCRIPTION_COPIES[fullPostType],
		postPreviewComponent: () => {
			return POST_PREVIEWS[fullPostType](props);
		},
		nextStepsComponent: () => {
			return NextSteps(fullPostType);
		},
		secondaryCTA: SECONDARY_CTAS[fullPostType],
		primaryCTA: PRIMARY_CTAS[fullPostType],
	};
}
